import React, { useState, useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
// Components
import Button from '@/components/newButton/Button'
import LandingPuppyCard from '@/components/landings/LandingPuppyCard'
import NotificationFormModal from '@/components/notificationFormModal/notificationFormModal'
import NotificationCard from '@/components/landings/NotificationCard'
// Utils
import { breedNameTextConverter } from '@/utils/converter'
import { getDefaultDiscountForApplication } from '@/utils/discounts'
import capitalize from '@/utils/capitalize'
import useMediaQuery from '@/utils/publicApi'
import { useDiscountContext } from '../../context/discountContext'
import styles from './Featured.module.scss'

type FeaturedProps = {
  title: string
  dogs: any[]
  breed: any
  variables: any
  landingType?: string
  city: string
}

const Featured = ({
  title,
  dogs: _dogs,
  breed,
  variables,
  landingType,
  city,
}: FeaturedProps) => {
  const router = useRouter()
  const { discount: contextDiscount } = useDiscountContext()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showNotificationForm, setShowNotificationForm] = useState(false)
  const isMobile = useMediaQuery('(max-width:600px)')

  const category = variables?.category || ''
  const stateName = variables?.stateName || ''
  const breedSlug = variables?.slug && capitalize(variables?.slug)
  const breedWithState = Object.entries(router.query).length
  const dogsAmount = _dogs?.length

  const breedNameTextConverterMemoized = useMemo(
    () => breedNameTextConverter(breed?.name),
    [breed?.name],
  )
  const dogs = useMemo(() => {
    return [..._dogs].slice(0, 36)
  }, [_dogs])
  // Layout logic
  const multi = !isMobile ? 4 : 2
  const areEven = dogs.length % multi === 0
  // handle view all link slugs
  const getViewAllLink = () => {
    if (breed?.slug && variables?.stateSlug)
      return `/search?breed=${breed?.slug}&state=${variables?.stateSlug}&sort=by-view-score`
    if (variables?.category)
      return `/search?category=${variables?.category}&sort=by-view-score`
    if (breed?.slug) return `/search?breed=${breed?.slug}&sort=by-view-score`
    if (variables?.stateSlug)
      return `/search?state=${variables?.stateSlug}&sort=by-view-score`
    return `/search?state=${variables?.stateSlug}&sort=by-view-score`
  }

  const viewAllLink = getViewAllLink()

  const serviceBannerMessageMap = {
    service:
      'These puppies are from breeds well-suited to be trained as service dogs.',
    therapy:
      'These puppies are from breeds well-suited to be trained as therapy dogs.',
    emotional_support:
      'These puppies are from companion breeds well-suited to be certified as Emotional Service Animals.',
    trained: 'These puppies are have completed obedience and potty training.',
  }

  const notificationMessage = () => {
    if (breedWithState >= 2 && !city)
      return `Get notified when ${breedNameTextConverterMemoized} puppies become available in ${stateName}.`
    if (breedWithState >= 2 && city)
      return `Get notified when ${breedNameTextConverterMemoized} puppies become available in ${city}, ${variables.state}.`
    if (stateName && !city)
      return `Get notified when more puppies become available in ${stateName}.`
    if (city)
      return `Get notified when more puppies become available in ${city}, ${variables.state}.`
    return `Get notified when ${breedNameTextConverterMemoized} puppies become available.`
  }

  const types = ['breedState', 'state', 'stateCity']

  return (
    <div className={styles.featuredContainer}>
      {showNotificationForm && (
        <NotificationFormModal
          isOpen={showNotificationForm}
          close={() => setShowNotificationForm(false)}
          setIsSubmitted={() => setIsSubmitted(true)}
          landingType={landingType}
          breedName={breed?.name}
          stateName={stateName}
          breedSlug={breedSlug}
        />
      )}
      <hr />

      {landingType === 'category' &&
        ['service', 'therapy', 'emotional_support', 'trained'].includes(
          category,
        ) && (
          <div className={styles.serviceBanner}>
            <p>
              <strong> {serviceBannerMessageMap[category]} </strong>
              <br />
              For personalized guidance, please call us at 1 (888) 488-7203.
            </p>
          </div>
        )}

      {/* Title */}
      <div className={styles.featuredTitle}>
        {dogsAmount > 0 && <h2>{title}</h2>}
        {dogsAmount >= 36 && (
          <Link href={viewAllLink} prefetch={false}>
            View All
          </Link>
        )}
      </div>

      {/* Puppy Cards Grid */}
      <div className={styles.featuredGrid}>
        {dogs?.map((dog) => {
          const initDiscount =
            getDefaultDiscountForApplication({ application: { dog } }) ||
            contextDiscount
          const discount = initDiscount?.applyDiscountByDefault
            ? initDiscount
            : undefined
          const discountPrice = discount
            ? `$${(dog?.salePrice?.subTotal - discount?.amount) / 100}`
            : dog?.salePrice?.puppyPrice
          const location =
            types.includes(landingType) || landingType === undefined
              ? `${dog?.breeder?.city}, ${dog?.breeder?.state}`
              : undefined
          const alt = `${dog?.name.substring(0, 15)} - ${dog?.breed?.name} ${dog?.gender}`
          const image = `${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${dog?.profilePicture?.small}`
          const gender = dog?.gender === 'Male' ? 'Boy' : 'Girl'

          return (
            <LandingPuppyCard
              key={dog?.id}
              name={dog?.name.substring(0, 18)}
              slug={dog?.slug}
              img={image}
              alt={alt}
              breed={dog?.breed?.name}
              gender={gender}
              salePrice={dog?.salePrice?.puppyPrice}
              discountPrice={discountPrice}
              discount={discount}
              location={location}
            />
          )
        })}
        {dogsAmount < 36 && (
          <NotificationCard
            message={notificationMessage()}
            setShowNotificationForm={setShowNotificationForm}
            isSubmitted={isSubmitted}
            areEven={areEven}
          />
        )}
      </div>

      {dogsAmount >= 36 && (
        <div className={styles.featuredButton}>
          <Button
            title="View more"
            theme={['green-solid', 'medium']}
            rightImage="/next-white.svg"
            altImg="White right arrow"
            width={10}
            height={10}
            onClick={() => router.push(viewAllLink)}
          />
        </div>
      )}
    </div>
  )
}

export default React.memo(Featured)
